import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { UserType } from '@econtractor/types';
import * as apiUser from 'api/apiUsers';
import { useToast } from 'context/ToastContext';
import UserNameItem from '../UsersRowItems/UserNameItem';
import SolarIcon from 'components/icons/SolarIcon';

export interface UserSelectedProps {
    value?: UserType | null;
    onChange?: (user: UserType | null) => void;
    title?: string;
    /** Елемент справа від заголовка (наприклад, «Додати себе») */
    rightElement?: ReactNode;
    /** Максимальна висота списку результатів (px) */
    maxHeight?: number;
    className?: string;
    disabled?: boolean;
    /** Кількість користувачів у відповіді пошуку */
    onPage?: number;
    /** Показати кнопку очищення вибраного користувача */
    clearable?: boolean;
    /** Компактний вигляд: менший аватар, лише ім'я */
    smallMode?: boolean;
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

const UserSelected = ({
    value = null,
    onChange,
    title,
    rightElement = null,
    maxHeight = 220,
    className = '',
    disabled = false,
    onPage = 50,
    clearable = true,
    smallMode = false,
    preload = false,
}: UserSelectedProps) => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [users, setUsers] = useState<UserType[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);

    if (preload) return null;

    const isFixed = Boolean(value);
    const showResults = loading || searched;

    const fetchUsers = async () => {
        if (disabled || loading || isFixed) return;

        setLoading(true);
        const res = await apiUser.getUsers({
            search: (search || '').trim(),
            page: 1,
            onPage,
        });
        setLoading(false);
        setSearched(true);

        if (!res) {
            setUsers([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setUsers(res.users || []);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchUsers();
        }
    };

    const handleSelect = (user: UserType) => {
        if (disabled) return;
        onChange?.(user);
        setSearch('');
        setUsers([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (disabled) return;
        onChange?.(null);
        setSearch('');
        setUsers([]);
        setSearched(false);
    };

    const labelText = title || t('Users.selectTitle');

    return (
        <div
            className={[
                'user-selected',
                smallMode ? 'user-selected--small' : undefined,
                className || 'mb-3',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {(labelText || rightElement) && (
                <div className="d-flex justify-content-between align-items-center">
                    <label
                        className={`form-label text-muted mb-1 ${smallMode ? 'fs-12' : ''} ${labelText ? '' : 'd-none'}`}
                    >
                        {labelText}
                    </label>
                    {rightElement}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user">
                        <UserNameItem user={value!} smallMode={smallMode} />
                    </div>
                    {clearable && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="user-selected-clear"
                            disabled={disabled}
                            title={t('common:buttons.clear')}
                            onClick={handleClear}
                        >
                            <SolarIcon name="x-lg" />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            size={smallMode ? 'sm' : undefined}
                            value={search}
                            disabled={disabled || loading}
                            placeholder={t('Users.selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('Users.selectSearchBtn')}
                            onClick={fetchUsers}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && users.length === 0 && (
                                <div className="user-selected-empty">
                                    <SolarIcon name="person-x" className="fs-5 d-block mb-1 opacity-50" />
                                    {t('Users.selectNoResults')}
                                </div>
                            )}

                            {!loading && users.length > 0 && (
                                <ul className="user-selected-list">
                                    {users.map((user) => (
                                        <li key={user.ID}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(user)}
                                            >
                                                <UserNameItem user={user} smallMode={smallMode} />
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserSelected;
